import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Bookmark, Camera, ChevronRight, ExternalLink, Inbox, Loader2, MoreHorizontal, Plus, Search,
  SlidersHorizontal, UserSearch, X,
} from 'lucide-react';
import { api } from '../utils/api';
import { useIsMobile } from '../utils/useIsMobile';
import { useAuth } from '../context/AuthContext';
import OrganicShell from '../components/organic/OrganicShell';
import MultiSelect from '../components/MultiSelect';
import ShareBrandModal from '../components/ShareBrandModal';
import { BrandTagEditor } from '../components/BrandTags';
import { CsvUploadModal, BulkResearchModal, ForceSocialSyncModal } from '../components/brands/BrandToolModals';
import PhotoCaptureSheet from '../components/brands/PhotoCaptureSheet';

// Brands — the searchable list, with Rankings folded in as a sort mode.
// A four-column table (down from ten); the other columns moved into the
// right-hand detail pane, and the eight filters moved behind a Filters
// button. Row click loads the pane; the pane links on to Brand detail.
//
// On a phone each row becomes a card carrying rank, growth and the
// secondary platform, and tapping one goes straight to Brand detail —
// there is no room beside the list for the detail pane. The filters
// come up in a sheet with its own apply button.

const STROKE = 2.75;
const PAGE_SIZE = 25;

const PERIOD_LABELS = { '7d': '7 days', '30d': '30 days', '90d': '90 days', '120d': '120 days' };
const PERIOD_OPTIONS = Object.entries(PERIOD_LABELS);
const PLATFORM_LABELS = { instagram: 'Instagram', tiktok: 'TikTok' };
const SORT_OPTIONS = [
  { value: 'growth_percentage', label: 'Growth %', subhead: 'growth' },
  { value: 'growth_absolute', label: 'Growth absolute', subhead: 'absolute growth' },
  { value: 'current_followers', label: 'Current followers', subhead: 'followers' },
  { value: 'recently_added', label: 'Recently added', subhead: 'date added' },
];

// `categories` and `countries` are sets: those filters take several
// values at once and the rankings endpoint matches a brand in any of
// them. Every other filter stays single-valued. Nothing may mutate this
// object — callers reset to it by reference, so the arrays are rebuilt
// on every change.
const DEFAULT_FILTERS = {
  period: '90d',
  platform: 'instagram',
  categories: [],
  countries: [],
  region: '',
  territory: '',
  hqState: '',
  tagId: '',
  foundedAfter: '',
  minFollowers: '',
};

// Where the last-used filters and sort live between visits. The key
// carries the active organisation: territories and tags are per-org
// ids, so one tenant's saved view must never be replayed against
// another's after an org switch or a different sign-in on the same
// browser. Stored values are also checked against the option lists so
// a stale or hand-edited entry can never put the page into a state the
// rankings endpoint would reject.
function viewStorageKey(user) {
  return `brands_view:${user?.active_org_id || user?.org_id || 'anon'}`;
}

function readStoredView(key) {
  const fallback = { filters: DEFAULT_FILTERS, sortBy: 'growth_percentage' };
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return fallback;
    const stored = JSON.parse(raw);
    const f = stored && typeof stored.filters === 'object' && stored.filters ? stored.filters : {};
    const filters = { ...DEFAULT_FILTERS };
    for (const key of Object.keys(DEFAULT_FILTERS)) {
      if (Array.isArray(DEFAULT_FILTERS[key])) {
        if (Array.isArray(f[key])) filters[key] = f[key].filter((v) => typeof v === 'string');
      } else if (typeof f[key] === 'string') {
        filters[key] = f[key];
      }
    }
    // A view saved when Category was a single pick carries it as a
    // string — keep that selection rather than dropping it.
    if (typeof f.category === 'string' && f.category && !filters.categories.length) {
      filters.categories = [f.category];
    }
    if (!PERIOD_LABELS[filters.period]) filters.period = DEFAULT_FILTERS.period;
    if (!PLATFORM_LABELS[filters.platform]) filters.platform = DEFAULT_FILTERS.platform;
    const sortBy = SORT_OPTIONS.some((o) => o.value === stored.sortBy)
      ? stored.sortBy : fallback.sortBy;
    return { filters, sortBy };
  } catch {
    return fallback; // Safari private mode throws on localStorage
  }
}

function writeStoredView(key, filters, sortBy) {
  try {
    window.localStorage.setItem(key, JSON.stringify({ filters, sortBy }));
  } catch {
    /* storage unavailable — the page still works, it just won't remember */
  }
}

// A multi-value filter on the "Active:" row, kept to one chip so the
// filter count doesn't jump by one per value: "Canada", then "Canada,
// France", then "Canada, France +2".
function listChip(values) {
  if (values.length <= 2) return values.join(', ');
  return `${values.slice(0, 2).join(', ')} +${values.length - 2}`;
}

// Everything the search box should match. Region, category and
// location are all on screen in the table, so a search for a value the
// user can see has to find it — matching the name alone made "asia
// pacific" return nothing for a brand whose Region column says exactly
// that. Former names are included so a rebranded company is findable
// under what people still call it.
function brandHaystack(brand) {
  return [
    brand.name,
    brand.category,
    brand.region,
    brand.country,
    brand.hq_state,
    brand.hq_city,
    brand.parent_company,
    brand.former_names,
  ].filter(Boolean).join(' ').toLowerCase();
}

function formatNumber(num) {
  if (num == null) return '—';
  if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
  if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
  return num.toLocaleString();
}

function growthLabel(pct) {
  if (pct == null) return '—';
  return `${pct >= 0 ? '+' : ''}${pct.toFixed(1)}%`;
}

// Weekly follower series (ascending) from raw metric snapshots.
function weeklySeries(metrics) {
  const byWeek = new Map();
  for (const m of metrics) {
    if (m.followers == null || !m.recorded_at) continue;
    const d = new Date(m.recorded_at);
    const key = `${d.getUTCFullYear()}-${Math.floor((d.getTime() / 86400000 + 4) / 7)}`;
    byWeek.set(key, { t: d.getTime(), followers: m.followers });
  }
  return [...byWeek.values()].sort((a, b) => a.t - b.t).map((v) => v.followers);
}

function AddFromWebsiteDialog({ onClose, onAdded }) {
  const [url, setUrl] = useState('');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!url.trim()) return;
    setBusy(true);
    setMessage(null);
    try {
      const result = await api.researchWebsite(url.trim());
      setMessage({ ok: true, text: result.message || 'Research started — the brand appears once fields are filled.' });
      setUrl('');
      onAdded?.();
    } catch (err) {
      setMessage({ ok: false, text: err.message });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="dialog-backdrop" onClick={onClose}>
      <form className="dialog" onClick={(e) => e.stopPropagation()} onSubmit={submit}>
        <div className="dialog-title">Add from website</div>
        <div className="dialog-body">
          Paste the brand's website. Research fills the profile — category,
          socials, HQ, founders — before it lands in the list.
        </div>
        <input
          className="input"
          type="url"
          required
          placeholder="https://drinkexample.com"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          autoFocus
        />
        {message && (
          <p style={{ margin: 0, fontSize: 13, color: message.ok ? 'var(--color-accent-2-700)' : 'var(--color-accent-700)' }}>
            {message.text}
          </p>
        )}
        <div className="dialog-actions">
          <button type="button" className="btn btn-secondary" onClick={onClose}>Close</button>
          <button type="submit" className="btn btn-primary" disabled={busy}>
            {busy ? <Loader2 size={14} strokeWidth={STROKE} className="spin" /> : <Plus size={14} strokeWidth={STROKE} />}
            {busy ? 'Researching…' : 'Research & add'}
          </button>
        </div>
      </form>
    </div>
  );
}

function FiltersPanel({ draft, setDraft, options, onReset, onApply, onClose }) {
  const field = (label, control) => (
    <div className="field">
      <label>{label}</label>
      {control}
    </div>
  );
  const set = (key) => (e) => setDraft((d) => ({ ...d, [key]: e.target.value }));

  return (
    <div className="card elev-md" style={{ padding: 'var(--space-4)', gap: 'var(--space-4)' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <h4 style={{ margin: 0 }}>Filters</h4>
        <button type="button" className="btn btn-icon btn-secondary" title="Close" aria-label="Close filters" onClick={onClose}>
          <X size={16} strokeWidth={STROKE} />
        </button>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: 'var(--space-3)' }}>
        {field('Period', (
          <select className="input" value={draft.period} onChange={set('period')}>
            {PERIOD_OPTIONS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
          </select>
        ))}
        {field('Platform', (
          <select className="input" value={draft.platform} onChange={set('platform')}>
            <option value="instagram">Instagram</option>
            <option value="tiktok">TikTok</option>
          </select>
        ))}
        {field('Categories', (
          <MultiSelect
            options={options.categories}
            selected={draft.categories}
            onChange={(categories) => setDraft((d) => ({ ...d, categories }))}
            allLabel="All categories"
            summaryNoun="categories"
            searchPlaceholder="Search categories…"
            ariaLabel="Filter by categories"
          />
        ))}
        {field('Countries', (
          <MultiSelect
            options={options.countries}
            selected={draft.countries}
            onChange={(countries) => setDraft((d) => ({ ...d, countries }))}
            allLabel="All countries"
            summaryNoun="countries"
            searchPlaceholder="Search countries…"
            ariaLabel="Filter by countries"
          />
        ))}
        {field('Region', (
          <select className="input" value={draft.region} onChange={set('region')}>
            <option value="">All regions</option>
            {options.regions.map((r) => <option key={r.slug} value={r.slug}>{r.label}</option>)}
          </select>
        ))}
        {options.territories.length > 0 && field('Territory', (
          <select className="input" value={draft.territory} onChange={set('territory')}>
            <option value="">All territories</option>
            {options.territories.map((t) => (
              <option key={t.id} value={t.id}>
                {t.owner_name ? `${t.name} · ${t.owner_name}` : t.name}
              </option>
            ))}
            <option value="unassigned">Unassigned</option>
          </select>
        ))}
        {field('HQ state', (
          <select className="input" value={draft.hqState} onChange={set('hqState')}>
            <option value="">All states</option>
            {options.states.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        ))}
        {field('Tag', (
          <select className="input" value={draft.tagId} onChange={set('tagId')}>
            <option value="">All tags</option>
            {options.tags.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
        ))}
        {field('Founded in or after', (
          <input className="input" type="number" placeholder="e.g. 2018" min="1800" max="2100" value={draft.foundedAfter} onChange={set('foundedAfter')} />
        ))}
        {field('Minimum followers', (
          <input className="input" type="number" placeholder="e.g. 5000" min="0" step="1000" value={draft.minFollowers} onChange={set('minFollowers')} />
        ))}
      </div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--space-2)' }}>
        <button type="button" className="btn btn-secondary" onClick={onReset}>Reset</button>
        <button type="button" className="btn btn-primary" onClick={onApply}>Apply</button>
      </div>
    </div>
  );
}

// The phone stand-in for FiltersPanel: the same nine filters plus the
// sort, one per row, applied by the sticky button at the foot. Period
// and platform keep their segmented form — they are short choices
// worth showing in full.
function FiltersSheet({ draft, setDraft, options, sortBy, setSortBy, onReset, onApply, onClose }) {
  const set = (key) => (e) => setDraft((d) => ({ ...d, [key]: e.target.value }));

  const segmented = (key, opts) => (
    <div className="seg o-seg-wide">
      {opts.map(([value, label]) => (
        <label key={value} className="seg-opt">
          <input
            type="radio"
            name={`sheet-${key}`}
            checked={draft[key] === value}
            onChange={() => setDraft((d) => ({ ...d, [key]: value }))}
          />
          {label}
        </label>
      ))}
    </div>
  );

  const row = (label, control) => (
    <div className="field" key={label}>
      <label>{label}</label>
      {control}
    </div>
  );

  return (
    <>
      <div className="o-sheet-backdrop" onClick={onClose} />
      <div className="o-sheet" role="dialog" aria-modal="true" aria-label="Filters">
        <div className="o-sheet-head">
          <span className="o-sheet-grip" />
          <div className="o-sheet-title-row">
            <h3 style={{ margin: 0 }}>Filters</h3>
            <button type="button" className="btn btn-ghost" onClick={onReset}>Reset</button>
          </div>
        </div>

        <div className="o-sheet-body">
          {row('Period', segmented('period', PERIOD_OPTIONS))}
          {row('Platform', segmented('platform', [['instagram', 'Instagram'], ['tiktok', 'TikTok']]))}
          {row('Rank by', (
            <select className="input" value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
              {SORT_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          ))}
          {row('Categories', (
            <MultiSelect
              options={options.categories}
              selected={draft.categories}
              onChange={(categories) => setDraft((d) => ({ ...d, categories }))}
              allLabel="All categories"
              summaryNoun="categories"
              searchPlaceholder="Search categories…"
              ariaLabel="Filter by categories"
            />
          ))}
          {row('Countries', (
            <MultiSelect
              options={options.countries}
              selected={draft.countries}
              onChange={(countries) => setDraft((d) => ({ ...d, countries }))}
              allLabel="All countries"
              summaryNoun="countries"
              searchPlaceholder="Search countries…"
              ariaLabel="Filter by countries"
            />
          ))}
          {row('Region', (
            <select className="input" value={draft.region} onChange={set('region')}>
              <option value="">All regions</option>
              {options.regions.map((r) => <option key={r.slug} value={r.slug}>{r.label}</option>)}
            </select>
          ))}
          {options.territories.length > 0 && row('Territory', (
            <select className="input" value={draft.territory} onChange={set('territory')}>
              <option value="">All territories</option>
              {options.territories.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.owner_name ? `${t.name} · ${t.owner_name}` : t.name}
                </option>
              ))}
              <option value="unassigned">Unassigned</option>
            </select>
          ))}
          {row('HQ state', (
            <select className="input" value={draft.hqState} onChange={set('hqState')}>
              <option value="">All states</option>
              {options.states.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          ))}
          {row('Tag', (
            <select className="input" value={draft.tagId} onChange={set('tagId')}>
              <option value="">All tags</option>
              {options.tags.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          ))}
          {row('Founded in or after', (
            <input className="input" type="number" inputMode="numeric" placeholder="e.g. 2018" min="1800" max="2100" value={draft.foundedAfter} onChange={set('foundedAfter')} />
          ))}
          {row('Minimum followers', (
            <input className="input" type="number" inputMode="numeric" placeholder="e.g. 5000" min="0" step="1000" value={draft.minFollowers} onChange={set('minFollowers')} />
          ))}
        </div>

        <div className="o-sheet-actions">
          <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button type="button" className="btn btn-primary" onClick={onApply}>Apply filters</button>
        </div>
      </div>
    </>
  );
}

function DetailPane({ item, filters, onClose, onTagsUpdated, onFollow }) {
  const navigate = useNavigate();
  const brand = item.brand;
  const growth = item.growth;
  const secondary = item.secondary_growth;
  const [series, setSeries] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setSeries(null);
    api.getMetrics({ brand_id: brand.id, platform: filters.platform, days: 90 })
      .then((metrics) => {
        if (!cancelled) setSeries(weeklySeries(Array.isArray(metrics) ? metrics : metrics.metrics || []));
      })
      .catch(() => { if (!cancelled) setSeries([]); });
    return () => { cancelled = true; };
  }, [brand.id, filters.platform]);

  // Growth vs the brand's own four-week average, from the weekly series.
  const multiple = useMemo(() => {
    if (!series || series.length < 3) return null;
    const growths = series.slice(1)
      .map((v, i) => (series[i] ? ((v - series[i]) / series[i]) * 100 : null))
      .filter((g) => g != null);
    if (growths.length < 2) return null;
    const last = growths[growths.length - 1];
    const window = growths.slice(-5, -1);
    const baseline = window.reduce((a, b) => a + b, 0) / window.length;
    return baseline > 0 ? (last / baseline).toFixed(1) : null;
  }, [series]);

  const chart = useMemo(() => {
    if (!series || series.length < 2) return null;
    const window = series.slice(-12);
    const min = Math.min(...window);
    const max = Math.max(...window);
    const span = max - min || 1;
    return window.map((v) => 15 + ((v - min) / span) * 85);
  }, [series]);

  const secondaryLabel = filters.platform === 'instagram' ? 'TikTok' : 'Instagram';
  const location = [brand.hq_city, brand.country].filter(Boolean).join(', ');

  const statCard = (kicker, value, note, valueStyle) => (
    <div className="card" style={{ gap: 2, padding: 'var(--space-3)', background: 'var(--color-bg)' }}>
      <span className="card-kicker">{kicker}</span>
      <span style={{ fontFamily: 'var(--font-heading)', fontSize: 24, ...valueStyle }}>{value}</span>
      <span style={{ fontSize: 11, opacity: 0.6 }}>{note}</span>
    </div>
  );

  return (
    <aside
      style={{
        width: 420, flex: 'none', background: 'var(--color-surface)',
        padding: 'var(--space-6) var(--space-4)', display: 'flex', flexDirection: 'column',
        gap: 'var(--space-4)', position: 'sticky', top: 0, height: '100vh',
        overflowY: 'auto', boxShadow: 'var(--shadow-lg)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 'var(--space-3)' }}>
        <div style={{ minWidth: 0 }}>
          <h3 style={{ margin: 0 }}>{brand.name}</h3>
          <p style={{ margin: '2px 0 0', fontSize: 13, opacity: 0.7 }}>
            {brand.category}{location ? ` · ${location}` : ''}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 6, flex: 'none' }}>
          <Link to={`/brands/${brand.id}`} className="btn btn-icon btn-secondary" title="Open full brand page" aria-label="Open full brand page">
            <ExternalLink size={16} strokeWidth={STROKE} />
          </Link>
          <button type="button" className="btn btn-icon btn-secondary" title="Close panel" aria-label="Close panel" onClick={onClose}>
            <X size={16} strokeWidth={STROKE} />
          </button>
        </div>
      </div>

      <BrandTagEditor brand={brand} onBrandUpdated={onTagsUpdated} />

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-3)' }}>
        {statCard('Followers', formatNumber(growth?.current_followers), PLATFORM_LABELS[filters.platform])}
        {statCard(
          `Growth, ${PERIOD_LABELS[filters.period]}`,
          growthLabel(growth?.growth_percentage),
          growth?.growth_absolute != null ? `${growth.growth_absolute >= 0 ? '+' : ''}${growth.growth_absolute.toLocaleString()} followers` : '—',
          { color: 'var(--color-accent-2-700)' },
        )}
        {statCard('Vs baseline', multiple ? `${multiple}×` : '—', '4-week average')}
        {statCard(
          secondaryLabel,
          growthLabel(secondary?.growth_percentage),
          secondary?.current_followers != null ? `${formatNumber(secondary.current_followers)} on ${secondaryLabel}` : `no ${secondaryLabel} data`,
        )}
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
          <h5 style={{ margin: 0 }}>Followers, 12 weeks</h5>
        </div>
        {chart ? (
          <>
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: 5, height: 96, padding: 'var(--space-2) 0' }}>
              {chart.map((h, i) => (
                <div
                  key={i}
                  style={{
                    flex: 1, borderRadius: '999px 999px 4px 4px', height: `${h}%`,
                    background: i >= chart.length - 2 ? 'var(--color-accent-2)' : 'var(--color-neutral-300)',
                  }}
                />
              ))}
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, opacity: 0.55 }}>
              <span>12 weeks ago</span>
              <span>this week</span>
            </div>
          </>
        ) : (
          <p style={{ margin: 0, fontSize: 13, opacity: 0.6 }}>
            {series === null ? 'Loading history…' : 'Not enough weekly history to chart yet.'}
          </p>
        )}
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)', marginTop: 'auto' }}>
        <button
          type="button"
          className="btn btn-primary"
          style={{ width: '100%' }}
          onClick={() => navigate(`/brands/${brand.id}#contacts`)}
        >
          <UserSearch size={16} strokeWidth={STROKE} /> Find contact
        </button>
        <button type="button" className="btn btn-secondary" style={{ width: '100%' }} onClick={() => onFollow(brand)}>
          <Bookmark size={16} strokeWidth={STROKE} /> Follow this brand
        </button>
      </div>
    </aside>
  );
}

function Brands() {
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  // Filters and sort survive leaving the page — opening a brand and
  // coming back keeps "90 days · Instagram · Europe" rather than
  // dropping to the defaults. Clear still resets to DEFAULT_FILTERS.
  const { user } = useAuth();
  const viewKey = viewStorageKey(user);
  const [storedView] = useState(() => readStoredView(viewKey));
  const [filters, setFilters] = useState(storedView.filters);
  const [draft, setDraft] = useState(storedView.filters);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [sortBy, setSortBy] = useState(storedView.sortBy);
  useEffect(() => { writeStoredView(viewKey, filters, sortBy); }, [viewKey, filters, sortBy]);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);

  const [rankings, setRankings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [options, setOptions] = useState({
    categories: [], countries: [], regions: [], states: [], tags: [], territories: [],
  });
  const [pendingCount, setPendingCount] = useState(null);

  const [selectedId, setSelectedId] = useState(null);
  const [paneOpen, setPaneOpen] = useState(false);
  const [following, setFollowing] = useState(null);
  const [dialog, setDialog] = useState(null); // 'add' | 'photo' | 'csv' | 'bulk' | 'sync'
  const [toolsOpen, setToolsOpen] = useState(false);

  // `/brands?scan=1` — the installed app's "Scan a product" shortcut —
  // opens the camera sheet straight away, then drops the param so a
  // refresh or back-navigation doesn't reopen it.
  const [searchParams, setSearchParams] = useSearchParams();
  const [scanFromLink, setScanFromLink] = useState(false);
  useEffect(() => {
    if (searchParams.get('scan') === '1') {
      setScanFromLink(true);
      setDialog('photo');
      const next = new URLSearchParams(searchParams);
      next.delete('scan');
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const refreshPendingCount = useCallback(async () => {
    try {
      const pending = await api.getPendingBrands('pending');
      setPendingCount(Array.isArray(pending) ? pending.length : null);
    } catch {
      /* badge keeps its last value */
    }
  }, []);

  // Guards against out-of-order responses when filters/sort change
  // quickly — only the latest request may commit state.
  const loadSeq = React.useRef(0);

  const load = useCallback(async () => {
    const seq = ++loadSeq.current;
    try {
      setLoading(true);
      const founded = Number(filters.foundedAfter);
      const data = await api.getRankings({
        period: filters.period,
        platform: filters.platform,
        limit: 1000,
        include_unranked: true,
        category: filters.categories,
        country: filters.countries,
        region: filters.region || undefined,
        hq_state: filters.hqState || undefined,
        territory: filters.territory || undefined,
        tag_id: filters.tagId || undefined,
        founded_year_min: Number.isFinite(founded) && founded >= 1800 && founded <= 2100 ? founded : undefined,
        min_followers: filters.minFollowers ? Number(filters.minFollowers) : undefined,
        sort_by: sortBy === 'recently_added' ? 'growth_percentage' : sortBy,
      });
      if (loadSeq.current !== seq) return;
      setRankings(data.rankings || []);
      setError(null);
    } catch (err) {
      if (loadSeq.current === seq) setError(err.message);
    } finally {
      if (loadSeq.current === seq) setLoading(false);
    }
  }, [filters, sortBy]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    (async () => {
      try {
        const [cats, filterVals, tagData, territoryData, pending] = await Promise.all([
          api.getCategories().catch(() => ({ categories: [] })),
          api.getRankingFilters().catch(() => ({ countries: [], regions: [], states: [] })),
          api.listTags().catch(() => null),
          api.listTerritories().catch(() => null),
          api.getPendingBrands('pending').catch(() => null),
        ]);
        const tags = tagData?.tags || [];
        const territories = territoryData?.territories || [];
        setOptions({
          categories: cats.categories || [],
          countries: filterVals.countries || [],
          regions: filterVals.regions || [],
          states: filterVals.states || [],
          tags,
          territories,
        });
        // A remembered tag or territory that no longer exists (deleted,
        // or saved under another org) would 404 the rankings request
        // and strand the page in an error state — drop it. Skipped when
        // the list itself failed to load, since then nothing is known.
        const prune = (f) => {
          let next = f;
          if (tagData && f.tagId && !tags.some((t) => String(t.id) === String(f.tagId))) {
            next = { ...next, tagId: '' };
          }
          if (
            territoryData && f.territory && f.territory !== 'unassigned'
            && !territories.some((t) => String(t.id) === String(f.territory))
          ) {
            next = { ...next, territory: '' };
          }
          return next;
        };
        setFilters(prune);
        setDraft(prune);
        // The endpoint returns a bare array of pending rows.
        if (pending) setPendingCount(Array.isArray(pending) ? pending.length : null);
      } catch {
        /* dropdowns stay empty */
      }
    })();
  }, []);

  // Search and "recently added" are client-side over the fetched set.
  const visible = useMemo(() => {
    let rows = rankings;
    const q = search.trim().toLowerCase();
    if (q) rows = rows.filter((r) => brandHaystack(r.brand).includes(q));
    if (sortBy === 'recently_added') {
      rows = [...rows].sort((a, b) => new Date(b.brand.created_at || 0) - new Date(a.brand.created_at || 0));
    }
    return rows;
  }, [rankings, search, sortBy]);

  const pageRows = visible.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);
  const selected = useMemo(
    () => visible.find((r) => r.brand.id === selectedId) || null,
    [visible, selectedId],
  );

  useEffect(() => { setPage(0); }, [search, sortBy, filters]);

  const activeFilterChips = useMemo(() => {
    const chips = [PERIOD_LABELS[filters.period], PLATFORM_LABELS[filters.platform]];
    if (filters.categories.length) chips.push(listChip(filters.categories));
    if (filters.countries.length) chips.push(listChip(filters.countries));
    if (filters.region) {
      const region = options.regions.find((r) => r.slug === filters.region);
      chips.push(region ? region.label : filters.region);
    }
    if (filters.territory) {
      const territory = options.territories.find(
        (t) => String(t.id) === String(filters.territory),
      );
      chips.push(territory ? territory.name : 'Unassigned');
    }
    if (filters.hqState) chips.push(filters.hqState);
    if (filters.tagId) {
      const tag = options.tags.find((t) => String(t.id) === String(filters.tagId));
      chips.push(tag ? tag.name : 'Tag');
    }
    if (filters.foundedAfter) chips.push(`Founded ≥ ${filters.foundedAfter}`);
    if (filters.minFollowers) chips.push(`≥ ${Number(filters.minFollowers).toLocaleString()} followers`);
    return chips;
  }, [filters, options.regions, options.tags, options.territories]);

  const selectRow = (row) => {
    // No room for the detail pane on a phone — the row leads straight
    // to the full brand page instead.
    if (isMobile) {
      navigate(`/brands/${row.brand.id}`);
      return;
    }
    setSelectedId(row.brand.id);
    setPaneOpen(true);
  };

  const handleTagsUpdated = (updated) => {
    setRankings((prev) => prev.map((r) => (
      r.brand.id === updated.id ? { ...r, brand: { ...r.brand, ...updated } } : r
    )));
  };

  const sortOption = SORT_OPTIONS.find((o) => o.value === sortBy);
  const subhead = `${rankings.length} watched · ranked by ${sortOption.subhead} over ${PERIOD_LABELS[filters.period]} on ${PLATFORM_LABELS[filters.platform]}`;

  const pane = !isMobile && paneOpen && selected ? (
    <DetailPane
      item={selected}
      filters={filters}
      onClose={() => setPaneOpen(false)}
      onTagsUpdated={handleTagsUpdated}
      onFollow={(brand) => setFollowing(brand)}
    />
  ) : null;

  return (
    <OrganicShell active="brands" contentStyle={{ maxWidth: 'none', padding: 'var(--space-8)' }} aside={pane}>
      <header style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 'var(--space-4) var(--space-6)', flexWrap: 'wrap' }}>
        <div style={{ minWidth: 0, flex: '1 1 300px' }}>
          <h1 className="o-page-title">Brands</h1>
          <p className="o-page-subhead">{subhead}</p>
        </div>
        {/* The three actions keep their labels on desktop and shrink to
            44px icon targets on a phone, where the title has to share
            the row with them. */}
        <div style={{ display: 'flex', gap: 'var(--space-2)', flex: 'none', flexWrap: 'wrap', position: 'relative' }}>
          <Link
            to="/brands/research/pending"
            className={`btn btn-secondary${isMobile ? ' btn-icon' : ''}`}
            style={isMobile ? { width: 44, height: 44, position: 'relative' } : undefined}
            title="Review researched brands"
            aria-label={`Review researched brands${pendingCount > 0 ? ` (${pendingCount} waiting)` : ''}`}
          >
            <Inbox size={16} strokeWidth={STROKE} />
            {!isMobile && ' Review'}
            {pendingCount > 0 && (
              <span
                className="tag tag-accent"
                style={isMobile
                  ? {
                    position: 'absolute', top: -2, right: -2, minWidth: 20, height: 20,
                    padding: 0, justifyContent: 'center', borderRadius: 999,
                    background: 'var(--color-accent)', color: 'var(--color-bg)',
                  }
                  : { marginLeft: 4 }}
              >
                {pendingCount}
              </span>
            )}
          </Link>
          {/* Scan a product — the phone way in. Primary on a phone
              (that is where the camera is); on desktop it lives in the
              tools menu as a file picker. */}
          {isMobile && (
            <button
              type="button"
              className="btn btn-primary btn-icon"
              style={{ width: 44, height: 44 }}
              title="Scan a product"
              aria-label="Scan a product"
              onClick={() => { setScanFromLink(false); setDialog('photo'); }}
            >
              <Camera size={18} strokeWidth={STROKE} />
            </button>
          )}
          <button
            type="button"
            className={isMobile ? 'btn btn-secondary btn-icon' : 'btn btn-primary'}
            style={isMobile ? { width: 44, height: 44 } : undefined}
            title="Add from website"
            aria-label="Add from website"
            onClick={() => setDialog('add')}
          >
            <Plus size={16} strokeWidth={STROKE} />
            {!isMobile && ' Add from website'}
          </button>
          <button
            type="button"
            className="btn btn-icon btn-secondary"
            title="More tools"
            aria-label="More tools"
            aria-expanded={toolsOpen}
            style={isMobile ? { width: 44, height: 44 } : undefined}
            onClick={() => setToolsOpen((v) => !v)}
          >
            <MoreHorizontal size={16} strokeWidth={STROKE} />
          </button>
          {toolsOpen && (
            <div
              className="card elev-lg"
              style={{ position: 'absolute', right: 0, top: 'calc(100% + 6px)', zIndex: 50, padding: 'var(--space-2)', minWidth: 190 }}
            >
              {[
                !isMobile && ['Add from photo', () => { setScanFromLink(false); setDialog('photo'); }],
                ['Export CSV', async () => {
                  try {
                    const response = await api.exportBrands('csv');
                    const blob = await response.blob();
                    const url = window.URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = 'brands.csv';
                    a.click();
                  } catch (err) {
                    setError(`Export failed: ${err.message}`);
                  }
                }],
                ['Upload CSV', () => setDialog('csv')],
                ['Bulk deep research', () => setDialog('bulk')],
                ['Force social sync', () => setDialog('sync')],
              ].filter(Boolean).map(([label, action]) => (
                <button
                  key={label}
                  type="button"
                  className="o-account-menu-item"
                  onClick={() => { setToolsOpen(false); action(); }}
                >
                  {label}
                </button>
              ))}
            </div>
          )}
        </div>
      </header>

      {pendingCount > 0 && (
        <div
          className="card"
          role="status"
          style={{
            flexDirection: 'row', alignItems: 'center', gap: 'var(--space-4)',
            /* --space-5 doesn't exist, which made the whole shorthand
               invalid and left the banner with no padding at all. */
            padding: 'var(--space-4) var(--space-6)', background: 'var(--color-accent-100)',
            border: '1px solid var(--color-accent)', flexWrap: 'wrap',
          }}
        >
          <Inbox size={isMobile ? 20 : 28} strokeWidth={STROKE} style={{ color: 'var(--color-accent-700)', flex: 'none' }} />
          <div style={{ flex: '1 1 240px', minWidth: 0 }}>
            <div style={{ fontFamily: 'var(--font-heading)', fontSize: isMobile ? 15 : 20, color: 'var(--color-accent-800)' }}>
              You have {pendingCount} {pendingCount === 1 ? 'brand' : 'brands'} to review
            </div>
            {/* The explanation is a first-run aid — on a phone the line
                above and the button say enough. */}
            {!isMobile && (
              <p style={{ margin: '2px 0 0', fontSize: 13, color: 'var(--color-accent-800)', opacity: 0.8 }}>
                New brands from research are waiting for your approval before they join the list.
              </p>
            )}
          </div>
          <Link to="/brands/research/pending" className="btn btn-primary" style={{ flex: 'none' }}>
            Review
          </Link>
        </div>
      )}

      {isMobile ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
          <div
            style={{
              flex: 1, minWidth: 0, height: 44, display: 'flex', alignItems: 'center', gap: 8,
              padding: '0 16px', background: 'var(--color-surface)',
              border: '1px solid var(--color-divider)', borderRadius: 999,
            }}
          >
            <Search size={16} strokeWidth={STROKE} style={{ opacity: 0.55, flex: 'none' }} />
            <input
              placeholder={`Search ${rankings.length} brands…`}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              aria-label="Search brands by name, category or region"
              style={{
                flex: 1, minWidth: 0, border: 'none', background: 'transparent', outline: 'none',
                font: 'inherit', fontSize: 14, color: 'var(--color-text)',
              }}
            />
          </div>
          <button
            type="button"
            className="btn btn-secondary"
            style={{ height: 44, flex: 'none', padding: '0 16px' }}
            aria-expanded={filtersOpen}
            aria-label={`Filters (${activeFilterChips.length} active)`}
            onClick={() => { setDraft(filters); setFiltersOpen(true); }}
          >
            <SlidersHorizontal size={15} strokeWidth={STROKE} /> {activeFilterChips.length}
          </button>
        </div>
      ) : (
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
          <div style={{ position: 'relative', flex: '1 1 260px', minWidth: 0, display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
            <Search size={16} strokeWidth={STROKE} style={{ opacity: 0.55, flex: 'none' }} />
            <input
              className="input"
              placeholder={`Search ${rankings.length} brands by name, category, region…`}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              aria-label="Search brands"
            />
          </div>
          <div style={{ flex: 'none', display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
            <label htmlFor="rank-by" style={{ margin: 0, fontSize: 12, whiteSpace: 'nowrap', opacity: 0.7 }}>Rank by</label>
            <select
              id="rank-by"
              className="input"
              style={{ width: 'auto' }}
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
            >
              {SORT_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
          <button
            type="button"
            className="btn btn-secondary"
            style={{ flex: 'none' }}
            aria-expanded={filtersOpen}
            onClick={() => { setDraft(filters); setFiltersOpen((v) => !v); }}
          >
            <SlidersHorizontal size={15} strokeWidth={STROKE} /> Filters
            <span className="tag tag-accent" style={{ marginLeft: 4 }}>{activeFilterChips.length}</span>
          </button>
        </div>
      )}

      {/* The phone row drops the "Active:" prefix and the Clear button —
          the sheet's Reset does that job — and carries the sort and the
          result count on the right instead. */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', flexWrap: isMobile ? 'nowrap' : 'wrap', fontSize: isMobile ? 12 : 13, overflow: isMobile ? 'hidden' : undefined }}>
        {!isMobile && <span style={{ opacity: 0.6 }}>Active:</span>}
        {(isMobile ? activeFilterChips.slice(0, 2) : activeFilterChips).map((chip) => (
          <span key={chip} className="tag tag-accent" style={{ flex: 'none' }}>{chip}</span>
        ))}
        {isMobile ? (
          <span style={{ marginLeft: 'auto', opacity: 0.6, whiteSpace: 'nowrap' }}>
            {sortOption.label} · {visible.length}
          </span>
        ) : (
          <button
            type="button"
            className="btn btn-ghost"
            style={{ fontSize: 13 }}
            onClick={() => { setFilters(DEFAULT_FILTERS); setDraft(DEFAULT_FILTERS); }}
          >
            Clear
          </button>
        )}
      </div>

      {filtersOpen && (isMobile ? (
        <FiltersSheet
          draft={draft}
          setDraft={setDraft}
          options={options}
          sortBy={sortBy}
          setSortBy={setSortBy}
          onReset={() => setDraft(DEFAULT_FILTERS)}
          onApply={() => { setFilters(draft); setFiltersOpen(false); }}
          onClose={() => setFiltersOpen(false)}
        />
      ) : (
        <FiltersPanel
          draft={draft}
          setDraft={setDraft}
          options={options}
          onReset={() => setDraft(DEFAULT_FILTERS)}
          onApply={() => { setFilters(draft); setFiltersOpen(false); }}
          onClose={() => setFiltersOpen(false)}
        />
      ))}

      {error && (
        <div className="card" style={{ padding: 'var(--space-3)', color: 'var(--color-accent-700)', fontSize: 14 }}>
          Error: {error}
        </div>
      )}

      {isMobile ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
          {loading && rankings.length === 0 ? (
            <div className="card" style={{ flexDirection: 'row', alignItems: 'center', gap: 8, fontSize: 14, opacity: 0.75 }}>
              <Loader2 size={16} strokeWidth={STROKE} className="spin" /> Loading brands…
            </div>
          ) : (
            <>
              {pageRows.map((row, i) => {
                const rank = page * PAGE_SIZE + i;
                const secondaryLabel = filters.platform === 'instagram' ? 'TikTok' : 'Instagram';
                return (
                  <button
                    key={row.brand.id}
                    type="button"
                    className="o-row-card"
                    onClick={() => selectRow(row)}
                  >
                    <span
                      className="o-row-rank"
                      style={{
                        fontSize: rank < 3 ? 20 : 15,
                        color: rank < 3 ? 'var(--color-accent)' : 'color-mix(in srgb, var(--color-text) 55%, transparent)',
                      }}
                    >
                      #{rank + 1}
                    </span>
                    <span className="o-row-main">
                      <span className="o-row-name">{row.brand.name}</span>
                      <span className="o-row-sub">
                        {[row.brand.category, row.brand.region].filter(Boolean).join(' · ') || '—'}
                      </span>
                    </span>
                    <span className="o-row-right">
                      <span className="o-row-growth">{growthLabel(row.growth?.growth_percentage)}</span>
                      <span className="o-row-secondary">
                        {row.secondary_growth?.growth_percentage != null
                          ? `${secondaryLabel} ${growthLabel(row.secondary_growth.growth_percentage)}`
                          : 'no data'}
                      </span>
                    </span>
                    <span className="o-row-chevron">
                      <ChevronRight size={16} strokeWidth={STROKE} />
                    </span>
                  </button>
                );
              })}
              {pageRows.length === 0 && (
                <div className="card" style={{ fontSize: 14, opacity: 0.7, textAlign: 'center' }}>
                  No brands match the current search and filters.
                </div>
              )}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'var(--space-2)', paddingTop: 'var(--space-2)', fontSize: 12 }}>
                <span style={{ opacity: 0.6 }}>
                  {pageRows.length ? page * PAGE_SIZE + 1 : 0}–{page * PAGE_SIZE + pageRows.length} of {visible.length}
                </span>
                <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
                  <button type="button" className="btn btn-secondary" style={{ height: 44 }} disabled={page === 0} onClick={() => setPage((p) => p - 1)}>
                    Previous
                  </button>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    style={{ height: 44 }}
                    disabled={(page + 1) * PAGE_SIZE >= visible.length}
                    onClick={() => setPage((p) => p + 1)}
                  >
                    Next
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      ) : (
      <div className="card" style={{ padding: 'var(--space-4)' }}>
        {loading && rankings.length === 0 ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, opacity: 0.75 }}>
            <Loader2 size={16} strokeWidth={STROKE} className="spin" /> Loading brands…
          </div>
        ) : (
          <>
            <table className="table">
              <thead>
                <tr>
                  <th style={{ width: 64 }}>Rank</th>
                  <th>Brand</th>
                  <th style={{ width: 160 }}>Category</th>
                  <th style={{ width: 175 }}>Region</th>
                  <th style={{ textAlign: 'right', width: 130 }}>Growth %</th>
                  <th style={{ textAlign: 'right', width: 150 }}>
                    {filters.platform === 'instagram' ? 'TikTok' : 'Instagram'}
                  </th>
                </tr>
              </thead>
              <tbody>
                {pageRows.map((row, i) => {
                  const rank = page * PAGE_SIZE + i;
                  const isSelected = paneOpen && row.brand.id === selectedId;
                  return (
                    // The row-wide click is a mouse affordance only.
                    // It used to be the keyboard one too (role="button"
                    // + tabIndex), but ARIA treats a button's subtree as
                    // presentational, which would have hidden the tag
                    // controls below from screen readers. The brand name
                    // is the real control now — announced by name rather
                    // than by the whole row read out.
                    <tr
                      key={row.brand.id}
                      onClick={() => selectRow(row)}
                      style={{ cursor: 'pointer', background: isSelected ? 'var(--color-accent-100)' : 'transparent' }}
                    >
                      <td style={{
                        fontFamily: 'var(--font-heading)',
                        fontSize: rank < 3 ? 20 : 15,
                        color: rank < 3 ? 'var(--color-accent)' : 'color-mix(in srgb, var(--color-text) 55%, transparent)',
                      }}>
                        #{rank + 1}
                      </td>
                      <td>
                        {/* The chips are editable in place: tagging used
                            to mean selecting the row first and reaching
                            for the pane. The editor still swallows the
                            click so "+ Tag" doesn't also open the pane
                            underneath it. */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
                          <button
                            type="button"
                            className="o-cell-name"
                            aria-pressed={isSelected}
                            title={`Show ${row.brand.name} in the detail panel`}
                            onClick={(e) => { e.stopPropagation(); selectRow(row); }}
                          >
                            {row.brand.name}
                          </button>
                          <span onClick={(e) => e.stopPropagation()} role="presentation">
                            <BrandTagEditor brand={row.brand} onBrandUpdated={handleTagsUpdated} />
                          </span>
                        </div>
                      </td>
                      <td style={{ fontSize: 13, opacity: 0.8 }}>{row.brand.category || '—'}</td>
                      <td style={{ fontSize: 13, opacity: 0.8 }}>{row.brand.region || '—'}</td>
                      <td style={{ textAlign: 'right', fontFamily: 'var(--font-heading)', fontSize: 16, color: 'var(--color-accent-700)' }}>
                        {growthLabel(row.growth?.growth_percentage)}
                      </td>
                      <td style={{ textAlign: 'right', fontSize: 13, opacity: 0.7 }}>
                        {row.secondary_growth?.growth_percentage != null
                          ? `${filters.platform === 'instagram' ? 'TikTok' : 'Instagram'} ${growthLabel(row.secondary_growth.growth_percentage)}`
                          : 'no data'}
                      </td>
                    </tr>
                  );
                })}
                {pageRows.length === 0 && (
                  <tr>
                    <td colSpan={6} style={{ textAlign: 'center', opacity: 0.65, padding: 'var(--space-4)' }}>
                      No brands match the current search and filters.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingTop: 'var(--space-3)', fontSize: 13 }}>
              <span style={{ opacity: 0.6 }}>
                Showing {pageRows.length ? page * PAGE_SIZE + 1 : 0}–{page * PAGE_SIZE + pageRows.length} of {visible.length}
              </span>
              <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
                <button type="button" className="btn btn-secondary" style={{ fontSize: 13 }} disabled={page === 0} onClick={() => setPage((p) => p - 1)}>
                  Previous
                </button>
                <button
                  type="button"
                  className="btn btn-secondary"
                  style={{ fontSize: 13 }}
                  disabled={(page + 1) * PAGE_SIZE >= visible.length}
                  onClick={() => setPage((p) => p + 1)}
                >
                  Next
                </button>
              </div>
            </div>
          </>
        )}
      </div>
      )}

      {dialog === 'add' && <AddFromWebsiteDialog onClose={() => setDialog(null)} onAdded={load} />}
      {dialog === 'photo' && (
        <PhotoCaptureSheet
          autoOpenCamera={scanFromLink}
          onClose={() => setDialog(null)}
          onAdded={() => { load(); refreshPendingCount(); }}
        />
      )}
      {dialog === 'csv' && <CsvUploadModal onClose={() => setDialog(null)} onCompleted={load} />}
      {dialog === 'bulk' && <BulkResearchModal onClose={() => setDialog(null)} onCompleted={load} />}
      {dialog === 'sync' && <ForceSocialSyncModal onClose={() => setDialog(null)} onCompleted={load} />}
      {following && (
        <ShareBrandModal brand={following} onClose={() => setFollowing(null)} onShared={() => setFollowing(null)} />
      )}
    </OrganicShell>
  );
}

export default Brands;
